// src/shared/components/PageHeader.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MoreVertical } from 'lucide-react';

const PageHeader = ({
  title,
  subtitle,
  description,
  icon: Icon,
  color = 'primary',
  actions,
  breadcrumbs,
  onBack,
  backUrl,
  className = '',
  children,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backUrl) {
      navigate(backUrl);
    } else {
      navigate(-1);
    }
  };

  const colorClasses = {
    primary: {
      bg: 'bg-primary-50',
      text: 'text-primary-600',
      border: 'border-primary-200',
      icon: 'bg-primary-100 text-primary-600',
    },
    success: {
      bg: 'bg-green-50',
      text: 'text-green-600',
      border: 'border-green-200',
      icon: 'bg-green-100 text-green-600',
    },
    warning: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-600',
      border: 'border-yellow-200',
      icon: 'bg-yellow-100 text-yellow-600',
    },
    danger: {
      bg: 'bg-red-50',
      text: 'text-red-600',
      border: 'border-red-200',
      icon: 'bg-red-100 text-red-600',
    },
    info: {
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      border: 'border-blue-200',
      icon: 'bg-blue-100 text-blue-600',
    },
    purple: {
      bg: 'bg-purple-50',
      text: 'text-purple-600',
      border: 'border-purple-200',
      icon: 'bg-purple-100 text-purple-600',
    },
    orange: {
      bg: 'bg-orange-50',
      text: 'text-orange-600',
      border: 'border-orange-200',
      icon: 'bg-orange-100 text-orange-600',
    },
  };

  const theme = colorClasses[color] || colorClasses.primary;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center text-sm text-gray-500">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="mx-2 text-gray-400">/</span>}
              {crumb.url ? (
                <button
                  onClick={() => navigate(crumb.url)}
                  className="hover:text-primary-600 transition-colors"
                >
                  {crumb.label}
                </button>
              ) : (
                <span className={index === breadcrumbs.length - 1 ? 'text-gray-900 font-medium' : ''}>
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors group"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500 group-hover:text-gray-700" />
          </button>

          {/* Icon */}
          {Icon && (
            <div className={`p-3 rounded-xl ${theme.icon} flex-shrink-0`}>
              <Icon className="h-6 w-6" />
            </div>
          )}

          {/* Title Section */}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              {children}
            </div>
            {subtitle && (
              <p className="text-sm font-medium text-gray-500 mt-1">{subtitle}</p>
            )}
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        {actions && (
          <div className="flex items-center space-x-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

// Variant with gradient background
export const PageHeaderGradient = ({
  title,
  subtitle,
  description,
  icon: Icon,
  gradient = 'from-primary-600 to-primary-800',
  actions,
  onBack,
  className = '',
}) => {
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${gradient} p-8 ${className}`}>
      {/* Decorative Pattern */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full transform translate-x-32 -translate-y-32"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full transform -translate-x-24 translate-y-24"></div>
      
      <div className="relative z-10">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            {/* Back Button */}
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}

            {/* Icon */}
            {Icon && (
              <div className="p-3 bg-white/20 rounded-xl">
                <Icon className="h-6 w-6 text-white" />
              </div>
            )}

            {/* Title Section */}
            <div>
              <h1 className="text-2xl font-bold text-white">{title}</h1>
              {subtitle && (
                <p className="text-sm font-medium text-white/80 mt-1">{subtitle}</p>
              )}
              {description && (
                <p className="text-sm text-white/70 mt-1">{description}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          {actions && (
            <div className="flex items-center space-x-3">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Variant with stats
export const PageHeaderWithStats = ({
  title,
  description,
  icon: Icon,
  stats = [],
  actions,
  onBack,
  className = '',
}) => {
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Main Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          {/* Back Button */}
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
          )}

          {/* Icon */}
          {Icon && (
            <div className="p-3 bg-primary-100 rounded-xl">
              <Icon className="h-6 w-6 text-primary-600" />
            </div>
          )}

          {/* Title Section */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        {actions && (
          <div className="flex items-center space-x-3">
            {actions}
          </div>
        )}
      </div>

      {/* Stats */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  {stat.change && (
                    <p className={`text-xs mt-1 ${
                      stat.change > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change > 0 ? '+' : ''}{stat.change}% from last month
                    </p>
                  )}
                </div>
                {stat.icon && (
                  <div className={`p-3 rounded-lg ${stat.color || 'bg-primary-100'}`}>
                    <stat.icon className={`h-5 w-5 ${stat.iconColor || 'text-primary-600'}`} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Variant with tabs
export const PageHeaderWithTabs = ({
  title,
  description,
  icon: Icon,
  tabs = [],
  activeTab,
  onTabChange,
  actions,
  onBack,
  className = '',
}) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          {/* Back Button */}
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
          )}

          {/* Icon */}
          {Icon && (
            <div className="p-3 bg-primary-100 rounded-xl">
              <Icon className="h-6 w-6 text-primary-600" />
            </div>
          )}

          {/* Title Section */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        {actions && (
          <div className="flex items-center space-x-3">
            {actions}
          </div>
        )}
      </div>

      {/* Tabs */}
      {tabs.length > 0 && (
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={`
                    flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      )}
    </div>
  );
};

// Compact header variant
export const PageHeaderCompact = ({
  title,
  subtitle,
  icon: Icon,
  actions,
  onBack,
  className = '',
}) => {
  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className="flex items-center space-x-3">
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-4 w-4 text-gray-500" />
          </button>
        )}

        {/* Icon */}
        {Icon && (
          <div className="p-2 bg-primary-100 rounded-lg">
            <Icon className="h-4 w-4 text-primary-600" />
          </div>
        )}

        {/* Title */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {subtitle && (
            <p className="text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      {actions && (
        <div className="flex items-center space-x-2">
          {actions}
        </div>
      )}
    </div>
  );
};

// Default export
PageHeader.defaultProps = {
  color: 'primary',
};

export default PageHeader;